import React from "react";
import { Eye, Send, AlertTriangle, Search, Mail } from "lucide-react";
import { Modal } from "./Modal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { SEND_REASON_LABELS } from "../pages/RecallTab";

/**
 * SPT-PTE-2026-0054 — consolidated "Preview" and "Send Automated Emails"
 * actions for the Calibration Due page. Both modes share the same due-customer
 * list + resolved-recipient data; only the row controls differ:
 *   - mode="preview": pick a different contact per customer from their
 *     existing contact list (or add a new one) and Save it as the customer's
 *     primary contact — every future recall/Cal-Due email (and the Owner of
 *     Tool column) then resolves to it.
 *   - mode="send": one-by-one Send, a bulk "Send All", and a per-customer
 *     "Test → Elsa/Heike" send so staff can spot-check before the real send.
 */
function reasonText(skipReasons) {
  return Object.entries(skipReasons || {})
    .map(([k, n]) => `${SEND_REASON_LABELS[k] || k} (${n})`).join(", ");
}

export function CalDueRecipientsModal({ filter, days, mode, onClose }) {
  const isPreview = mode === "preview";
  const [loading, setLoading] = React.useState(true);
  const [customers, setCustomers] = React.useState([]); // {customer_id, customer_name, count}
  const [emails, setEmails] = React.useState({}); // {cid: {email, name, source}}
  const [cfg, setCfg] = React.useState({});
  const [q, setQ] = React.useState("");
  const [selected, setSelected] = React.useState(new Set());

  const [editingId, setEditingId] = React.useState(null);
  const [contactsCache, setContactsCache] = React.useState({}); // {cid: [contacts]}
  const [contactsLoading, setContactsLoading] = React.useState(false);
  const [pickedContactId, setPickedContactId] = React.useState("");
  const [newContact, setNewContact] = React.useState({ name: "", email: "" });
  const [saving, setSaving] = React.useState(false);

  const [sendingIds, setSendingIds] = React.useState(new Set());
  const [sendingAll, setSendingAll] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get("/calibration-due", { params: { filter, days, limit: 2000 } }),
      api.get("/recall/config"),
    ]).then(async ([dueRes, cfgRes]) => {
      if (!alive) return;
      const items = dueRes.data?.items || [];
      const m = new Map();
      items.forEach((it) => {
        const id = it.customer_id || "unknown";
        if (!m.has(id)) m.set(id, { customer_id: id, customer_name: it.customer_name || "Unknown customer", count: 0 });
        m.get(id).count += 1;
      });
      const list = [...m.values()].sort((a, b) => a.customer_name.localeCompare(b.customer_name));
      setCustomers(list);
      setSelected(new Set(list.map((c) => c.customer_id)));
      setCfg(cfgRes.data || {});
      if (list.length) {
        const r = await api.get("/recall/resolve-recipients", { params: { customer_ids: list.map((c) => c.customer_id).join(",") } });
        if (alive) setEmails(r.data?.items || {});
      }
    }).catch((e) => toast.error(apiErr(e))).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [filter, days]);

  const filtered = customers.filter((c) => c.customer_name.toLowerCase().includes(q.toLowerCase()));
  const allFilteredSel = filtered.length > 0 && filtered.every((c) => selected.has(c.customer_id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllFiltered = () => setSelected((s) => {
    const n = new Set(s);
    if (allFilteredSel) filtered.forEach((c) => n.delete(c.customer_id));
    else filtered.forEach((c) => n.add(c.customer_id));
    return n;
  });

  const openEdit = async (cid) => {
    setEditingId(cid);
    setPickedContactId("");
    setNewContact({ name: "", email: "" });
    if (!contactsCache[cid]) {
      setContactsLoading(true);
      try {
        const r = await api.get(`/customers/${cid}`);
        setContactsCache((c) => ({ ...c, [cid]: r.data?.contacts || [] }));
      } catch (e) { toast.error(apiErr(e)); }
      finally { setContactsLoading(false); }
    }
  };

  const saveContact = async (cid) => {
    if (!pickedContactId) { toast.error("Pick a contact first"); return; }
    setSaving(true);
    try {
      let contactId = pickedContactId;
      let payload;
      if (contactId === "__new__") {
        if (!newContact.name.trim()) { toast.error("Contact name is required"); setSaving(false); return; }
        const addRes = await api.post(`/customers/${cid}/contacts`, { name: newContact.name, email: newContact.email });
        contactId = addRes.data?.id;
        payload = { name: newContact.name, email: newContact.email || null, is_primary: true };
      } else {
        const c = (contactsCache[cid] || []).find((x) => x.id === contactId);
        if (!c) { toast.error("Pick a contact first"); setSaving(false); return; }
        payload = { name: c.name, email: c.email, phone: c.phone, is_primary: true };
      }
      await api.patch(`/customers/${cid}/contacts/${contactId}`, payload);
      const r = await api.get("/recall/resolve-recipients", { params: { customer_ids: cid } });
      setEmails((e) => ({ ...e, ...(r.data?.items || {}) }));
      setContactsCache((c) => ({ ...c, [cid]: undefined }));
      toast.success("Saved — this contact will now receive future Calibration Due emails");
      setEditingId(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const busyKey = (k) => sendingIds.has(k);
  const setBusyKey = (k, on) => setSendingIds((s) => { const n = new Set(s); on ? n.add(k) : n.delete(k); return n; });

  const sendOne = async (cid, name) => {
    setBusyKey(cid, true);
    try {
      const res = await api.post("/recall/send-selected", { window_days: days, customer_ids: [cid] });
      const { sent = 0, skipped = 0, failed = 0, skip_reasons = {} } = res.data || {};
      if (sent > 0) toast.success(`Emailed ${name}` + (cfg.test_mode ? ` (redirected — test mode is on)` : ""));
      else toast.error(`Not sent to ${name} — ${reasonText(skip_reasons) || `skipped ${skipped}, failed ${failed}`}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyKey(cid, false); }
  };

  const sendTest = async (cid, who) => {
    const override_email = who === "elsa" ? "elsa@ptecal.co.za" : "heike@ptecal.co.za";
    const key = `${cid}-${who}`;
    setBusyKey(key, true);
    try {
      const res = await api.post("/recall/send-customer", { customer_id: cid, window_days: days, override_email });
      if (res.data?.ok) toast.success(`Test email sent to ${who === "elsa" ? "Elsa" : "Heike"}`);
      else toast.error(res.data?.error || "Test send failed");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyKey(key, false); }
  };

  const sendAll = async () => {
    if (!selected.size) { toast.error("Select at least one customer."); return; }
    if (!window.confirm(`Send the calibration-due report to ${selected.size} customer(s)?` +
      (cfg.test_mode ? `\n\nTEST MODE is ON — every email will actually go to ${cfg.test_recipient || "the test recipient"} instead.` : ""))) return;
    setSendingAll(true);
    try {
      const res = await api.post("/recall/send-selected", { window_days: days, customer_ids: [...selected] });
      const { sent = 0, skipped = 0, failed = 0, skip_reasons = {} } = res.data || {};
      if (sent > 0) {
        toast.success(`Emailed ${sent} customer${sent === 1 ? "" : "s"}` +
          (skipped ? ` · ${skipped} skipped` : "") + (failed ? ` · ${failed} failed` : ""));
        onClose();
      } else toast.error(`No emails sent (${skipped} skipped, ${failed} failed)` +
        (reasonText(skip_reasons) ? ` — ${reasonText(skip_reasons)}` : "") + ".");
    } catch (e) { toast.error(apiErr(e)); } finally { setSendingAll(false); }
  };

  return (
    <Modal open onClose={onClose} icon={isPreview ? Eye : Send}
      title={isPreview ? "Preview — who will receive the Calibration Due report" : "Send Automated Emails"}
      subtitle={isPreview
        ? "Review each customer's recipient email and item count. Pick a different contact and save it for next time."
        : "Send each customer their calibration-due report, one at a time or all at once."}
      wide
      footer={<>
        <button className="btn ghost" onClick={onClose} data-testid="caldue-recip-close">Close</button>
        {!isPreview && (
          <button className="btn gold" onClick={sendAll} disabled={sendingAll || !selected.size} data-testid="caldue-recip-sendall">
            <Send size={14} /> {sendingAll ? "Sending…" : `Send All (${selected.size})`}
          </button>
        )}
      </>}>
      {cfg.test_mode && (
        <div className="card pad" style={{ background: "#fff7da", border: "1px solid #e3d28a",
          color: "#5a3f00", fontSize: 12.5, marginBottom: 12 }} data-testid="caldue-recip-test-banner">
          <AlertTriangle size={14} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          <b>TEST MODE is ON</b> — {isPreview ? "any emails sent right now" : "every email below"} will actually be delivered to{" "}
          <code>{cfg.test_recipient || "(no test recipient set)"}</code>, not the customer.
          Turn this off in Settings → Emails → Calibration Due once you're ready to send to real customers.
        </div>
      )}
      {loading ? (
        <p className="t-mut" style={{ padding: "10px 0" }}>Loading customers…</p>
      ) : customers.length === 0 ? (
        <p className="t-mut" style={{ padding: "10px 0" }}>No equipment due in this window.</p>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <Search size={14} style={{ position: "absolute", left: 9, top: 9, color: "var(--text-3)" }} />
              <input className="input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Filter customers…" style={{ width: "100%", paddingLeft: 28 }}
                data-testid="caldue-recip-search" />
            </div>
            {!isPreview && (
              <button className="btn ghost sm" onClick={toggleAllFiltered} data-testid="caldue-recip-selectall">
                {allFilteredSel ? "Clear" : "Select"} {q ? "shown" : "all"}
              </button>
            )}
          </div>
          <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8 }}>
            {filtered.map((c) => {
              const em = emails[c.customer_id] || {};
              const editing = editingId === c.customer_id;
              const contacts = contactsCache[c.customer_id];
              return (
                <div key={c.customer_id} data-testid={`caldue-recip-row-${c.customer_id}`}
                  style={{ borderBottom: "1px solid var(--line)", padding: "8px 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {!isPreview && (
                      <input type="checkbox" checked={selected.has(c.customer_id)}
                        onChange={() => toggle(c.customer_id)} data-testid={`caldue-recip-check-${c.customer_id}`} />
                    )}
                    <span className="t-strong" style={{ flex: 1 }}>{c.customer_name}</span>
                    <span className="t-mut mono" style={{ fontSize: 12 }}>
                      {em.email ? <a href={`mailto:${em.email}`}>{em.email}</a> : <span style={{ color: "var(--red, #b3261e)" }}>no email on file</span>}
                    </span>
                    <span className="t-mut" style={{ fontSize: 12, minWidth: 54, textAlign: "right" }}>{c.count} item{c.count === 1 ? "" : "s"}</span>
                    {isPreview ? (
                      <button className="btn ghost sm" onClick={() => (editing ? setEditingId(null) : openEdit(c.customer_id))}
                        data-testid={`caldue-recip-change-${c.customer_id}`}>{editing ? "Cancel" : "Change"}</button>
                    ) : (
                      <>
                        <button className="btn ghost sm" onClick={() => sendTest(c.customer_id, "elsa")}
                          disabled={busyKey(`${c.customer_id}-elsa`)} data-testid={`caldue-recip-test-elsa-${c.customer_id}`}>
                          <Mail size={12} /> Test → Elsa
                        </button>
                        <button className="btn ghost sm" onClick={() => sendTest(c.customer_id, "heike")}
                          disabled={busyKey(`${c.customer_id}-heike`)} data-testid={`caldue-recip-test-heike-${c.customer_id}`}>
                          <Mail size={12} /> Test → Heike
                        </button>
                        <button className="btn gold sm" onClick={() => sendOne(c.customer_id, c.customer_name)}
                          disabled={busyKey(c.customer_id)} data-testid={`caldue-recip-send-${c.customer_id}`}>
                          <Send size={12} /> {busyKey(c.customer_id) ? "Sending…" : "Send"}
                        </button>
                      </>
                    )}
                  </div>
                  {isPreview && editing && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, paddingLeft: 24, flexWrap: "wrap" }}>
                      <select className="input" style={{ maxWidth: 320 }} value={pickedContactId}
                        onChange={(e) => setPickedContactId(e.target.value)}
                        data-testid={`caldue-recip-contact-select-${c.customer_id}`}>
                        <option value="">{contactsLoading ? "Loading contacts…" : "Select a contact…"}</option>
                        {(contacts || []).map((ct) => (
                          <option key={ct.id} value={ct.id}>
                            {ct.name}{ct.email ? ` <${ct.email}>` : " (no email)"}{ct.is_primary ? " — current" : ""}
                          </option>
                        ))}
                        <option value="__new__">+ Add new contact…</option>
                      </select>
                      {pickedContactId === "__new__" && (
                        <>
                          <input className="input" style={{ maxWidth: 160 }} placeholder="Name"
                            value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                            data-testid={`caldue-recip-newname-${c.customer_id}`} />
                          <input className="input" style={{ maxWidth: 200 }} placeholder="Email"
                            value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                            data-testid={`caldue-recip-newemail-${c.customer_id}`} />
                        </>
                      )}
                      <button className="btn gold sm" onClick={() => saveContact(c.customer_id)} disabled={saving || !pickedContactId}
                        data-testid={`caldue-recip-save-${c.customer_id}`}>{saving ? "Saving…" : "Save"}</button>
                    </div>
                  )}
                </div>
              );
            })}
            {!filtered.length && <div className="t-mut" style={{ padding: 14, textAlign: "center" }}>No customers match "{q}".</div>}
          </div>
        </>
      )}
    </Modal>
  );
}
